"use client";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { Autocomplete, AutocompleteItem } from "@nextui-org/react";
import type { Key } from "@react-types/shared";
import { useTranslations } from "next-intl";

import { AutocompleteExtraOptions } from "@/data/helper/selectOptionsList";
import ArrowDownIcon from "@/icons/arrow_down-red.svg";
import { SelectOption } from "@/ui/Forms/FormSelect";
import FormWrapper from "@/ui/Forms/FormWrapper";
import { cn } from "@/utils/cn";

// Use SelectOption directly instead of empty interface
export type AutocompleteOption = SelectOption;

export interface FormAutocompleteProps {
  name: string;
  label?: string;
  placeholder?: string;
  options: AutocompleteOption[];
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  allowsCustomValue?: boolean;
  errorClassName?: string;
  isResetAfterChange?: boolean;
  extraOptions?: AutocompleteExtraOptions;
  isAutoSelectItem?: boolean;
}

/**
 * Helper function to extract string value from label (string | ReactNode)
 * For ReactNode labels, falls back to option.value for filtering
 */
const getStringFromLabel = (
  label: string | ReactNode,
  fallback: string,
): string => {
  if (typeof label === "string") {
    return label;
  }

  if (typeof label === "object") {
    // @ts-expect-error inject value from ReactNode
    return label?.props?.children?.[0].props.children ?? fallback;
  }

  return fallback;
};

/**
 * FormAutocomplete component
 * Dropdown list component with autocomplete using NextUI Autocomplete
 */
const FormAutocomplete = ({
  name,
  label,
  placeholder,
  options: mainOptions,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  className = "",
  allowsCustomValue = false,
  errorClassName,
  isResetAfterChange,
  extraOptions,
  isAutoSelectItem,
}: FormAutocompleteProps) => {
  const t = useTranslations();
  const [selectedValue, setSelectedValue] = useState<string>(value || "");
  const [inputValue, setInputValue] = useState<string>(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
      setInputValue(value);
    }
  }, [value]);

  const options = useMemo(() => {
    if (extraOptions) {
      if (value && value === inputValue) {
        return extraOptions.primaryItems;
      }

      if (inputValue.trim()) {
        return extraOptions.fullItems;
      }

      return extraOptions.primaryItems;
    }

    return mainOptions;
  }, [extraOptions, mainOptions, value, inputValue]);

  const handleSelectionChange = (key: Key | null) => {
    if (key === null) return;

    const newValue = key.toString();
    setSelectedValue(newValue);

    // Find the option to get its label for display
    const selectedOption = options.find((option) => option.value === newValue);
    const displayValue = selectedOption
      ? getStringFromLabel(selectedOption.label, selectedOption.value)
      : newValue;

    if (onChange) {
      onChange(newValue);
    }

    if (isResetAfterChange) {
      setInputValue("");
      setSelectedValue("");
      setIsOpen(false);
      autocompleteRef.current?.querySelector("input")?.blur();
    } else {
      setInputValue(displayValue);
    }
  };

  const handleInputChange = (inputValue: string) => {
    setInputValue(inputValue);

    if (allowsCustomValue && !isAutoSelectItem) {
      // For custom values, use the input directly as the value
      setSelectedValue(inputValue);
      if (onChange) {
        onChange(inputValue);
      }
    } else if (isAutoSelectItem) {
      // For non-custom values, only update if it matches an option
      const matchingOption = options.find((option) => {
        const labelString = getStringFromLabel(option.label, option.value);
        return labelString.toLowerCase() === inputValue.toLowerCase();
      });
      if (matchingOption) {
        setSelectedValue(matchingOption.value);
        if (onChange) {
          onChange(matchingOption.value);
        }
      }
    }
  };

  // Trigger for opening/closing the dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      const inputElement = autocompleteRef.current?.querySelector("input");
      if (inputElement) {
        if (isOpen) {
          inputElement.blur();
          setIsOpen(false);
        } else {
          inputElement.focus();
          setIsOpen(true);
        }
      }
    }
  };

  // Handler for open/close state changes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <FormWrapper
      label={label}
      error={error}
      required={required}
      className={cn("relative w-full", className)}
      errorClassName={errorClassName}
    >
      <div
        ref={autocompleteRef}
        className="form-autocomplete-container"
      >
        <Autocomplete
          inputValue={inputValue}
          onInputChange={handleInputChange}
          value={selectedValue}
          onSelectionChange={handleSelectionChange}
          selectedKey={selectedValue}
          placeholder={placeholder || t("Select")}
          isDisabled={disabled}
          radius="lg"
          variant="flat"
          aria-label={label || name}
          allowsCustomValue={allowsCustomValue}
          classNames={{
            base: "form-autocomplete-base",
            // innerWrapper: "!h-full",
            // input: "form-select-text",
            popoverContent: "form-popover-content",
            listboxWrapper: "form-listbox-wrapper",
            listbox: "form-listbox",
            clearButton: "!hidden",
            selectorButton: "!hidden",
          }}
          popoverProps={{
            shouldCloseOnScroll: false,
            classNames: {
              content: "form-popover-content",
            },
            onOpenChange: handleOpenChange,
          }}
          listboxProps={{
            emptyContent: (
              <div className="p-4 text-default-500 text-sm">
                {t("search.table.noResults")}
              </div>
            ),
            className: "form-listbox",
            itemClasses: {
              base: "form-autocomplete-item",
            },
          }}
          isInvalid={!!error}
          size="lg"
          onOpenChange={handleOpenChange}
        >
          {options.map((option) => {
            const textValue = getStringFromLabel(option.label, option.value);
            return (
              <AutocompleteItem
                key={textValue}
                value={option.value}
                textValue={textValue}
                startContent={
                  option.icon && (
                    <span className="form-option-icon">{option.icon}</span>
                  )
                }
              >
                <span>{option.label}</span>
              </AutocompleteItem>
            );
          })}
        </Autocomplete>
        <div
          className={cn(
            "form-custom-selector-wrapper",
            isOpen && "form-custom-selector-wrapper-open",
          )}
          onClick={toggleDropdown}
        >
          <ArrowDownIcon className="form-select-arrow" />
        </div>
      </div>
    </FormWrapper>
  );
};

export default FormAutocomplete;
